/**
 * The layout tree: where each field of a record's form shows, and in which
 * section. Decisions 12 to 14 of docs/plans/entry-lines-and-layout.md.
 *
 * It lives in `app.record.prefs.layout`, beside `prefs.capture`, and changes
 * only WHERE a field shows. What an entry saves is the field's business
 * (`showWhen`, `required`), never the layout's: a field the tree leaves out is
 * not gone, it goes at the end of the form, so a new field never disappears.
 *
 * Three pieces, all pure, so the server, the phone and the web run one copy:
 *
 *   readLayout   the stored document, judged: a tree this build can draw, or
 *                null, and then the form is drawn the way it always was
 *   keepLayoutFields  the same tree with the nodes of fields a caller may not
 *                see taken out, before it leaves the server
 *   formPlan     the sections the form draws and the field ids in each, from a
 *                schema and a tree (or no tree). The phone's entry form and
 *                the web's both draw this plan; the web keeps its own spans.
 */

/** The layout version this build reads. A higher one is a tree it cannot draw. */
export const LAYOUT_VERSION = 1;

/** How deep sections may nest: a section, and a section inside it. */
export const LAYOUT_SECTION_DEPTH_MAX = 2;

/** How many nodes one tree may hold. A record holds tens of fields, not hundreds. */
export const LAYOUT_NODES_MAX = 400;

/** How long a section title may be. */
export const LAYOUT_TITLE_MAX = 80;

export type LayoutSectionStyle = 'plain' | 'card' | 'folded';
export type LayoutSpan = 'auto' | 'full';

export type LayoutNode =
  | {
      readonly kind: 'section';
      readonly title: string | null;
      readonly style: LayoutSectionStyle;
      readonly children: readonly LayoutNode[];
    }
  | { readonly kind: 'field'; readonly fieldId: string; readonly span: LayoutSpan };

export type RecordLayout = { readonly version: 1; readonly nodes: readonly LayoutNode[] };

/** Why a stored or sent tree does not read. */
export type LayoutRefusal =
  | 'LAYOUT_SHAPE'
  | 'LAYOUT_VERSION_NEW'
  | 'LAYOUT_NODE_UNKNOWN'
  | 'LAYOUT_FIELD_UNKNOWN'
  | 'LAYOUT_FIELD_TWICE'
  | 'LAYOUT_TOO_DEEP'
  | 'LAYOUT_TOO_BIG';

export type LayoutJudgement =
  | { readonly ok: true; readonly layout: RecordLayout }
  | { readonly ok: false; readonly code: LayoutRefusal; readonly fieldId: string | null };

export type ReadLayoutOptions = {
  /**
   * Ids whose nodes are dropped without a word: the record's archived fields.
   * Nothing strips `prefs.layout` when a field is archived, so a tree that
   * named one must still read, or every archive would put the form back to
   * its plain order.
   */
  readonly dropped?: ReadonlySet<string>;
};

const SECTION_STYLES: readonly string[] = ['plain', 'card', 'folded'];
const SPANS: readonly string[] = ['auto', 'full'];

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

class Refused extends Error {
  constructor(
    readonly code: LayoutRefusal,
    readonly fieldId: string | null = null,
  ) {
    super(code);
  }
}

/**
 * The tree judged against the fields it may name, with the reason when it does
 * not read. `readLayout` is the answer most callers want; this one is for the
 * structure save, which must say why it refuses.
 *
 * `fieldIds` is every live field of the record, switched on or off: a field
 * switched off is still the record's, and naming it must not null the tree.
 * The form leaves it out because the schema does, not because the tree does.
 */
export const judgeLayout = (
  raw: unknown,
  fieldIds: Iterable<string>,
  options: ReadLayoutOptions = {},
): LayoutJudgement => {
  const known = new Set(fieldIds);
  const dropped = options.dropped ?? new Set<string>();
  const seen = new Set<string>();
  let count = 0;

  const readNodes = (list: unknown, depth: number): LayoutNode[] => {
    if (!Array.isArray(list)) throw new Refused('LAYOUT_SHAPE');
    const out: LayoutNode[] = [];
    for (const node of list) {
      count += 1;
      if (count > LAYOUT_NODES_MAX) throw new Refused('LAYOUT_TOO_BIG');
      if (!isObject(node)) throw new Refused('LAYOUT_SHAPE');
      if (node.kind === 'field') {
        const { fieldId, span } = node;
        if (typeof fieldId !== 'string' || fieldId === '') throw new Refused('LAYOUT_SHAPE');
        if (typeof span !== 'string' || !SPANS.includes(span)) throw new Refused('LAYOUT_SHAPE', fieldId);
        if (dropped.has(fieldId) && !known.has(fieldId)) continue;
        if (!known.has(fieldId)) throw new Refused('LAYOUT_FIELD_UNKNOWN', fieldId);
        if (seen.has(fieldId)) throw new Refused('LAYOUT_FIELD_TWICE', fieldId);
        seen.add(fieldId);
        out.push({ kind: 'field', fieldId, span: span as LayoutSpan });
        continue;
      }
      if (node.kind === 'section') {
        if (depth + 1 > LAYOUT_SECTION_DEPTH_MAX) throw new Refused('LAYOUT_TOO_DEEP');
        const { title, style } = node;
        if (title !== null && typeof title !== 'string') throw new Refused('LAYOUT_SHAPE');
        if (typeof title === 'string' && title.length > LAYOUT_TITLE_MAX) throw new Refused('LAYOUT_SHAPE');
        if (typeof style !== 'string' || !SECTION_STYLES.includes(style)) throw new Refused('LAYOUT_SHAPE');
        const trimmed = typeof title === 'string' ? title.trim() : null;
        out.push({
          kind: 'section',
          title: trimmed === null || trimmed === '' ? null : trimmed,
          style: style as LayoutSectionStyle,
          children: readNodes(node.children, depth + 1),
        });
        continue;
      }
      throw new Refused('LAYOUT_NODE_UNKNOWN');
    }
    return out;
  };

  try {
    if (!isObject(raw)) throw new Refused('LAYOUT_SHAPE');
    const { version } = raw;
    if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
      throw new Refused('LAYOUT_SHAPE');
    }
    if (version > LAYOUT_VERSION) throw new Refused('LAYOUT_VERSION_NEW');
    return { ok: true, layout: { version: LAYOUT_VERSION, nodes: readNodes(raw.nodes, 0) } };
  } catch (error) {
    if (error instanceof Refused) return { ok: false, code: error.code, fieldId: error.fieldId };
    throw error;
  }
};

/**
 * The stored tree, or null when it does not read: an unknown node kind, an
 * unknown field id, a field that shows twice, sections deeper than two, a
 * version higher than this build knows, or a document of any other shape.
 * Null is not an error to the person: the form is then drawn as it is today.
 */
export const readLayout = (
  raw: unknown,
  fieldIds: Iterable<string>,
  options: ReadLayoutOptions = {},
): RecordLayout | null => {
  const judged = judgeLayout(raw, fieldIds, options);
  return judged.ok ? judged.layout : null;
};

/**
 * The tree with the node of every field `keep` refuses taken out: a masked
 * field, a switched-off one, a line's parent and root. A section that held
 * fields and is left with none goes too, so its title does not tell a viewer
 * what the record keeps behind a mask. A section the owner left empty stays;
 * the form does not draw it either way.
 */
export const keepLayoutFields = (
  layout: RecordLayout,
  keep: (fieldId: string) => boolean,
): RecordLayout => {
  const fieldsIn = (node: LayoutNode): number =>
    node.kind === 'field' ? 1 : node.children.reduce((sum, child) => sum + fieldsIn(child), 0);
  const strip = (nodes: readonly LayoutNode[]): LayoutNode[] =>
    nodes.flatMap((node): LayoutNode[] => {
      if (node.kind === 'field') return keep(node.fieldId) ? [node] : [];
      const children = strip(node.children);
      const after: LayoutNode = { ...node, children };
      return fieldsIn(node) > 0 && fieldsIn(after) === 0 ? [] : [after];
    });
  return { version: layout.version, nodes: strip(layout.nodes) };
};

/* ------------------------------------------------------------- the plan */

export type PlannedField = { readonly kind: 'field'; readonly fieldId: string; readonly span: LayoutSpan };

export type PlannedItem = PlannedField | { readonly kind: 'section'; readonly section: PlannedSection };

export type PlannedSection = {
  /** Stable within one tree: the section's place in it, or `rest` for the end. */
  readonly key: string;
  readonly title: string | null;
  readonly style: LayoutSectionStyle;
  /**
   * True for a section the tree did not name: the loose fields at its top
   * level, the fields it leaves out, or the whole form when there is no tree.
   * It draws as the plain form has always drawn, with no heading.
   */
  readonly implicit: boolean;
  /** Its fields and its inner sections, in the order the tree gives. */
  readonly items: readonly PlannedItem[];
  /** Every field it draws, inner sections included, in drawing order. */
  readonly fieldIds: readonly string[];
};

/** What `formPlan` needs from a schema: its fields, in the form's order (`ord`). */
export type FormPlanSchema = { readonly fields: ReadonlyArray<{ readonly id: string }> };

const implicitSection = (key: string, fields: readonly PlannedField[]): PlannedSection => ({
  key,
  title: null,
  style: 'plain',
  implicit: true,
  items: fields,
  fieldIds: fields.map((field) => field.fieldId),
});

/**
 * The sections a form draws, and the fields in each.
 *
 * - No tree, or a tree of a version this build does not know (an old build can
 *   hold a newer schema in its cache): one implicit section of every field, in
 *   schema order. The form as it is today.
 * - A tree: its sections in its order. Loose fields at its top level gather
 *   into implicit sections between the named ones. A field the tree names that
 *   the schema does not carry (masked, switched off) is left out.
 * - Every schema field the tree does not name goes at the end, so a new field
 *   never disappears from the form.
 * - A section with no field to draw does not show. `shown` says which fields
 *   draw right now (`showWhen`, a hidden dependent choice); a field it hides
 *   keeps its place in the tree and does not fall to the end.
 *
 * The first field of the first section is "the top" (`formPlanTop`): the one
 * place the amount draws large and opens the keypad (Decision 21).
 */
export const formPlan = (
  schema: FormPlanSchema,
  layout: RecordLayout | null | undefined,
  shown: (fieldId: string) => boolean = () => true,
): readonly PlannedSection[] => {
  const order = schema.fields.map((field) => field.id);
  const known = new Set(order);
  const placed = new Set<string>();

  const looseField = (id: string): PlannedField => ({ kind: 'field', fieldId: id, span: 'auto' });

  if (layout === null || layout === undefined || layout.version !== LAYOUT_VERSION) {
    const all = order.filter(shown).map(looseField);
    return all.length === 0 ? [] : [implicitSection('rest', all)];
  }

  const fieldItem = (node: Extract<LayoutNode, { kind: 'field' }>): PlannedField | null => {
    if (!known.has(node.fieldId) || placed.has(node.fieldId)) return null;
    placed.add(node.fieldId);
    return shown(node.fieldId) ? { kind: 'field', fieldId: node.fieldId, span: node.span } : null;
  };

  const sectionOf = (node: Extract<LayoutNode, { kind: 'section' }>, key: string): PlannedSection | null => {
    const items: PlannedItem[] = [];
    const fieldIds: string[] = [];
    node.children.forEach((child, index) => {
      if (child.kind === 'field') {
        const item = fieldItem(child);
        if (item === null) return;
        items.push(item);
        fieldIds.push(item.fieldId);
        return;
      }
      const inner = sectionOf(child, `${key}.${index}`);
      if (inner === null) return;
      items.push({ kind: 'section', section: inner });
      fieldIds.push(...inner.fieldIds);
    });
    if (fieldIds.length === 0) return null;
    return { key, title: node.title, style: node.style, implicit: false, items, fieldIds };
  };

  const plan: PlannedSection[] = [];
  let loose: PlannedField[] = [];
  let looseKey = '';
  const flushLoose = (): void => {
    if (loose.length > 0) plan.push(implicitSection(looseKey, loose));
    loose = [];
  };
  layout.nodes.forEach((node, index) => {
    if (node.kind === 'field') {
      if (loose.length === 0) looseKey = `f${index}`;
      const item = fieldItem(node);
      if (item !== null) loose.push(item);
      return;
    }
    flushLoose();
    const section = sectionOf(node, `s${index}`);
    if (section !== null) plan.push(section);
  });
  flushLoose();

  const rest = order.filter((id) => !placed.has(id) && shown(id)).map(looseField);
  if (rest.length > 0) {
    const last = plan[plan.length - 1];
    if (last !== undefined && last.implicit) {
      plan[plan.length - 1] = implicitSection(last.key, [...(last.items as PlannedField[]), ...rest]);
    } else {
      plan.push(implicitSection('rest', rest));
    }
  }
  return plan;
};

/** The field at the top of the form, or null for a form with nothing to draw. */
export const formPlanTop = (plan: readonly PlannedSection[]): string | null => plan[0]?.fieldIds[0] ?? null;

/**
 * The `lines` fields whose section already says their name: a field set
 * straight in a named section titled as the field is ("Rooms" holding the
 * Rooms lines). The section's title is the heading there, so the run of lines
 * draws no second one under it. The phone's form and the web's both read it.
 */
export const linesNamedBySectionOf = (
  plan: readonly PlannedSection[],
  labelOf: (fieldId: string) => string | undefined,
  isLines: (fieldId: string) => boolean,
): ReadonlySet<string> => {
  const named = new Set<string>();
  const same = (a: string, b: string): boolean => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
  const walk = (section: PlannedSection): void => {
    for (const item of section.items) {
      if (item.kind === 'section') {
        walk(item.section);
        continue;
      }
      const label = labelOf(item.fieldId);
      if (section.title === null || label === undefined || !isLines(item.fieldId)) continue;
      if (same(section.title, label)) named.add(item.fieldId);
    }
  };
  for (const section of plan) walk(section);
  return named;
};

/* ------------------------------------------------- editing a tree by hand */

/**
 * One section of a tree as Customize lists it: its key (the same key
 * `formPlan` gives it, its place in the tree), its title, how deep it sits
 * (1 at the top level, 2 inside another) and the fields it holds directly.
 *
 * The owner edits a tree one field at a time (Step E5 of
 * docs/plans/entry-lines-and-layout.md): move a field to another section,
 * rename a section, or take the tree off. Every edit below returns a new tree
 * and leaves the one it was given alone, so a draft can compare the two.
 */
export type LayoutSectionRow = {
  readonly key: string;
  readonly title: string | null;
  readonly style: LayoutSectionStyle;
  readonly depth: 1 | 2;
  readonly fieldIds: readonly string[];
};

const sectionKeyOf = (parent: string | null, index: number): string =>
  parent === null ? `s${index}` : `${parent}.${index}`;

/** Every section of the tree, in drawing order, an inner one after its parent. */
export const layoutSectionsOf = (layout: RecordLayout | null | undefined): readonly LayoutSectionRow[] => {
  if (layout === null || layout === undefined) return [];
  const rows: LayoutSectionRow[] = [];
  const walk = (nodes: readonly LayoutNode[], parent: string | null, depth: 1 | 2): void => {
    nodes.forEach((node, index) => {
      if (node.kind !== 'section') return;
      const key = sectionKeyOf(parent, index);
      rows.push({
        key,
        title: node.title,
        style: node.style,
        depth,
        fieldIds: node.children.flatMap((child) => (child.kind === 'field' ? [child.fieldId] : [])),
      });
      if (depth === 1) walk(node.children, key, 2);
    });
  };
  walk(layout.nodes, null, 1);
  return rows;
};

/** The key of the section that holds this field directly, or null when none does. */
export const layoutSectionOfField = (layout: RecordLayout | null | undefined, fieldId: string): string | null =>
  layoutSectionsOf(layout).find((row) => row.fieldIds.includes(fieldId))?.key ?? null;

/**
 * The tree with one field moved to the end of another section, or, with
 * `sectionKey` null, out of every section: a field the tree does not name
 * goes at the end of the form. A key the tree does not hold leaves the tree
 * as it was, so a stale tap never loses a field. So does the section that
 * already holds the field (or null for a field no section holds): choosing
 * where a field already is must not send it to the end and push another
 * field, the amount perhaps, to the top.
 */
export const moveLayoutField = (
  layout: RecordLayout,
  fieldId: string,
  sectionKey: string | null,
): RecordLayout => {
  if (sectionKey !== null && !layoutSectionsOf(layout).some((row) => row.key === sectionKey)) return layout;
  if (layoutSectionOfField(layout, fieldId) === sectionKey) return layout;
  const walk = (nodes: readonly LayoutNode[], parent: string | null): LayoutNode[] => {
    const out: LayoutNode[] = [];
    nodes.forEach((node, index) => {
      if (node.kind === 'field') {
        if (node.fieldId !== fieldId) out.push(node);
        return;
      }
      const key = sectionKeyOf(parent, index);
      const children = walk(node.children, key);
      if (key === sectionKey) children.push({ kind: 'field', fieldId, span: 'auto' });
      out.push({ ...node, children });
    });
    return out;
  };
  return { version: layout.version, nodes: walk(layout.nodes, null) };
};

/**
 * The tree with one field moved to the top of the form: the head of the first
 * section, or in front of everything when the tree starts with a loose field.
 * It is "Move it back to the top" for the amount on a form with sections
 * (Decision 21), which `moveLayoutField` cannot do, since it only appends. A
 * field already there leaves the tree as it was.
 */
export const moveLayoutFieldToTop = (layout: RecordLayout, fieldId: string): RecordLayout => {
  const first = layout.nodes[0];
  const isHere = (node: LayoutNode | undefined): boolean => node?.kind === 'field' && node.fieldId === fieldId;
  if (isHere(first) || (first?.kind === 'section' && isHere(first.children[0]))) return layout;
  let span: LayoutSpan = 'auto';
  const without = (nodes: readonly LayoutNode[]): LayoutNode[] =>
    nodes.flatMap((node): LayoutNode[] => {
      if (node.kind !== 'field') return [{ ...node, children: without(node.children) }];
      if (node.fieldId !== fieldId) return [node];
      span = node.span;
      return [];
    });
  const nodes = without(layout.nodes);
  const head: LayoutNode = { kind: 'field', fieldId, span };
  const lead = nodes[0];
  if (lead?.kind === 'section') nodes[0] = { ...lead, children: [head, ...lead.children] };
  else nodes.unshift(head);
  return { version: layout.version, nodes };
};

/** The tree with one section's title changed. A blank title is no title. */
export const renameLayoutSection = (layout: RecordLayout, sectionKey: string, title: string): RecordLayout => {
  const trimmed = title.trim().slice(0, LAYOUT_TITLE_MAX);
  const walk = (nodes: readonly LayoutNode[], parent: string | null): LayoutNode[] =>
    nodes.map((node, index): LayoutNode => {
      if (node.kind === 'field') return node;
      const key = sectionKeyOf(parent, index);
      return {
        ...node,
        title: key === sectionKey ? (trimmed === '' ? null : trimmed) : node.title,
        children: walk(node.children, key),
      };
    });
  return { version: layout.version, nodes: walk(layout.nodes, null) };
};
